import { DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { Button, message, Modal, Select, Space, Tag } from 'antd';
import { seasonService } from 'content/service';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SeasonListItem } from 'type/seasonType';
import * as Component from '../../../../shared/components';
import { SERVICE_DISTRIBUTION_OPTIONS, STATUS_OPTIONS } from '../../movie/constants';

const INGEST_SOURCE_OPTIONS = [
  { label: 'In-House', value: 'in-house' },
  { label: 'Mediahold', value: 'mediahold' },
  { label: 'KCP', value: 'kcp' },
];

const Season: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams({
    page: '1',
    page_size: '100',
    status: '',
  });
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [_selectedList, setSelectedList] = useState<number[]>([]);
  const [serviceDistributionFilter, setServiceDistributionFilter] = useState<string[]>(
    searchParams.get('service_distribution')?.split(',').filter(Boolean) || []
  );
  const [ingestSourceFilter, setIngestSourceFilter] = useState<string[]>(
    searchParams.get('ingest_source')?.split(',').filter(Boolean) || []
  );
  const [statusFilter, setStatusFilter] = useState<string | null>(searchParams.get('status'));

  const handleServiceDistributionChange = (values: string[]) => {
    setServiceDistributionFilter(values);
    const newParams = new URLSearchParams(searchParams);
    if (values.length > 0) {
      newParams.set('service_distribution', values.join(','));
    } else {
      newParams.delete('service_distribution');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleIngestSourceFilterChange = (values: string[]) => {
    setIngestSourceFilter(values);
    const newParams = new URLSearchParams(searchParams);
    if (values.length > 0) {
      newParams.set('ingest_source', values.join(','));
    } else {
      newParams.delete('ingest_source');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('status', value);
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  // Get English poster thumbnails (landscape and portrait)
  const getEnglishPosters = (posters?: any[]) => {
    if (!posters) return { landscape: null, portrait: null };
    const englishPosters = posters.filter(
      p => p.language?.toUpperCase() === 'EN' || p.language?.toUpperCase() === 'ENGLISH'
    );
    return {
      landscape: englishPosters.find(p => p.orientation === 'landscape'),
      portrait: englishPosters.find(p => p.orientation === 'portrait'),
    };
  };
  const columns = [
    {
      title: 'POSTER',
      dataIndex: 'posters',
      key: 'poster',
      width: '20%',
      render: (_text: string, record: SeasonListItem) => {
        const posters = getEnglishPosters(record.posters);
        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {posters.landscape && (
              <img
                src={posters.landscape.file || ''}
                alt="landscape"
                style={{ width: '80px', height: '45px', objectFit: 'cover', borderRadius: '4px' }}
              />
            )}
            {posters.portrait && (
              <img
                src={posters.portrait.file || ''}
                alt="portrait"
                style={{ width: '35px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
              />
            )}
            {!posters.landscape && !posters.portrait && (
              <div style={{ color: '#999', fontSize: '12px' }}>No poster</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'TITLE',
      dataIndex: 'title',
      key: 'title',
      width: '25%',
      sorter: true,
      render: (_text: string, record: SeasonListItem) => {
        const enTitle = record.i18n_title?.en || record.title_en || '';
        const koTitle = record.i18n_title?.ko || '';
        const zhTitle = record.i18n_title?.['zh-hans'] || '';
        return (
          <div
            onClick={() => window.open(`/season/edit2/${record.id}`, '_blank')}
            style={{ cursor: 'pointer', color: '#1890ff' }}
          >
            {enTitle && <div style={{ fontWeight: 'bold' }}>{enTitle}</div>}
            {(koTitle || zhTitle) && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {koTitle}
                {koTitle && zhTitle && ' / '}
                {zhTitle}
              </div>
            )}
            {!enTitle && !koTitle && !zhTitle && <div style={{ color: '#999' }}>Untitled</div>}
          </div>
        );
      },
    },
    {
      title: 'SLUG',
      dataIndex: 'slug',
      key: 'slug',
      width: '15%',
      sorter: true,
    },
    // {
    //   title: 'STATUS',
    //   dataIndex: 'status',
    //   key: 'status',
    //   width: '5%',
    //   render: (status: string) => {
    //     const statusMap: Record<string, { color: string; text: string }> = {
    //       published: { color: '#52c41a', text: 'enabled' },
    //       unpublished: { color: '#ff4d4f', text: 'disabled' },
    //     };
    //     const statusInfo = statusMap[status] || { color: '#999', text: status || 'disabled' };
    //     return <span style={{ color: statusInfo.color }}>{statusInfo.text}</span>;
    //   },
    // },
    {
      title: 'INGEST SOURCE',
      dataIndex: 'ingest_source',
      key: 'ingest_source',
      width: '10%',
      render: (ingestSource: string | null | undefined) => {
        if (!ingestSource) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        const colorMap: Record<string, string> = {
          mediahold: 'blue',
          kcp: 'purple',
          'in-house': 'green',
        };
        return (
          <Tag color={colorMap[ingestSource.toLowerCase()] || 'default'}>
            {ingestSource.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'ACTIONS',
      dataIndex: 'id',
      key: 'actions',
      width: '10%',
      render: (_: any, record: SeasonListItem) => (
        <Space>
          <Button
            type="text"
            icon={<SettingOutlined />}
            onClick={() => window.open(`/season/edit2/${record.id}`, '_blank')}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              Modal.confirm({
                title: 'Are you sure you want to delete?',
                content: 'This action cannot be undone.',
                okText: 'Delete',
                okType: 'danger',
                cancelText: 'Cancel',
                onOk: async () => {
                  try {
                    await seasonService.delete(record.id);
                    message.success('Successfully deleted.');
                    window.location.href = '/season/';
                  } catch (error) {
                    console.error('Failed to delete season:', error);
                  }
                },
              });
            }}
            disabled={!record.is_deletable}
          />
        </Space>
      ),
    },
  ];

  const fetchSeasonTableData = useCallback(
    async (params: URLSearchParams): Promise<{ data: SeasonListItem[]; count: number }> => {
      try {
        const result = await seasonService.fetchSeasonList(params);

        // SeasonListSerializer already includes posters, no separate call needed
        return {
          data: result.results,
          count: result.count,
        };
      } catch (error) {
        console.error('Failed to fetch season list:', error);
        // Fallback: use existing fetch
        const result = await seasonService.fetch(params);
        return {
          data: result.results.map((s: any) => ({
            id: s.id,
            slug: s.slug,
            status: s.status === 'published' ? 'published' : 'unpublished',
            title: s.title,
            title_en: s.title_en,
            posters: s.posters || [],
            season_order: s.season_order,
            is_deletable: s.is_deletable,
          })) as SeasonListItem[],
          count: result.count,
        };
      }
    },
    [searchParams]
  );

  useEffect(() => {
    setRefreshCounter(prev => prev + 1);
  }, [searchParams]);

  return (
    <Component.BasePageComponent>
      <div className="season-page">
        {/* Filter Section */}
        <div
          style={{
            marginBottom: '16px',
            padding: '16px',
            backgroundColor: '#fafafa',
            borderRadius: '4px',
            border: '1px solid #d9d9d9',
          }}
        >
          <Space wrap size="middle">
            <div>
              <div style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                Service Distribution
              </div>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="All Sources"
                value={serviceDistributionFilter}
                onChange={handleServiceDistributionChange}
                options={SERVICE_DISTRIBUTION_OPTIONS}
              />
            </div>
            <div>
              <div style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                Ingest Source
              </div>
              <Select
                mode="multiple"
                style={{ width: 200 }}
                placeholder="All Sources"
                value={ingestSourceFilter}
                onChange={handleIngestSourceFilterChange}
                options={INGEST_SOURCE_OPTIONS}
              />
            </div>
            {/* <div>
              <div style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Status</div>
              <Select
                style={{ width: 150 }}
                placeholder="All Status"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                options={STATUS_OPTIONS}
              />
            </div> */}
          </Space>
        </div>

        {/* Table */}
        <Component.TableComponent<SeasonListItem>
          key={refreshCounter}
          name="Season"
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          dataFetch={fetchSeasonTableData}
          columns={columns}
          actions={{
            onSelectionChange: setSelectedList,
            // onAddClick: () => navigate('/season/edit2/new'),
          }}
          additionalButton={[]}
        />
      </div>
    </Component.BasePageComponent>
  );
};

export default observer(Season);
